import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { supabase, isSupabaseConfigured, setForceDemoMode } from 'lib/supabase';

// ---------------------------------------------------------------------------
// LoginForm — landing-page sign-in
//
// Two audiences hit this page:
//
//   A. A real customer (teacher, admin, therapist, para) whose school
//      or district has already deployed the app. They arrive knowing
//      their email and password. Sign-in should be the FIRST thing
//      they see, front and center — not buried under demo buttons.
//
//   B. A prospect (district director, sped coordinator) evaluating the
//      app before they buy. They don't have an account and shouldn't
//      need one to look around. They tap a demo button and are dropped
//      into a canned role instantly.
//
// The previous version (pre-Aug 2026) buried sign-in under the demo
// buttons, which meant a district evaluator on their first visit could
// tap "Special Ed Teacher" thinking it was a real sign-in for their
// role, then wonder why their real students didn't appear. Every button
// is now labeled "Try demo — <role>" so it's unmistakable that these
// are previews of the app, not sign-in shortcuts. The sign-in form
// itself is now the default when Supabase is configured live, so real
// customers don't have to hunt for it.
// ---------------------------------------------------------------------------

const DEMO_ACCOUNTS = [
  {
    role: 'teacher',
    label: 'Try demo — Teacher view',
    description: 'See a caseload of 6 students with real-looking IEP goals.',
    email: 'sarah.johnson@edutracker.edu',
    password: 'Teacher123!',
    icon: 'GraduationCap',
    color: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    role: 'admin',
    label: 'Try demo — Admin view',
    description: 'Principal / district — see all teachers, all students.',
    email: 'admin@edutracker.edu',
    password: 'Admin123!',
    icon: 'Building2',
    color: 'bg-purple-600 hover:bg-purple-700',
  },
  {
    role: 'paraprofessional',
    label: 'Try demo — Para view',
    description: "Support staff — log data alongside a teacher's caseload.",
    email: 'para@edutracker.edu',
    password: 'Para123!',
    icon: 'HeartHandshake',
    color: 'bg-teal-600 hover:bg-teal-700',
  },
  {
    role: 'related_service',
    label: 'Try demo — SLP / OT / PT view',
    description: 'Speech, OT, PT, behavior specialist service caseload.',
    email: 'therapist@edutracker.edu',
    password: 'Service123!',
    icon: 'Stethoscope',
    color: 'bg-emerald-600 hover:bg-emerald-700',
  },
];

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [demoBusyRole, setDemoBusyRole] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const supabaseLive = isSupabaseConfigured();

  const roleOptions = [
    {
      value: 'teacher',
      label: 'Special Education Teacher',
      description: 'You write IEPs and run a caseload or classroom.',
    },
    {
      value: 'admin',
      label: 'Administrator',
      description: 'Principal, program director, district-level access.',
    },
    {
      value: 'paraprofessional',
      label: 'Paraprofessional',
      description: 'You support students and log data alongside the teacher.',
    },
    {
      value: 'related_service',
      label: 'SLP / OT / PT / Behavior Specialist',
      description: 'You see students on your service caseload.',
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // One-click demo entry. No typing required.
  //
  // IMPORTANT: this path deliberately bypasses Supabase even on a
  // live-Supabase deployment. The canned emails
  // (sarah.johnson@edutracker.edu etc.) are not real users in anybody's
  // production Supabase project, so calling signInWithPassword with them
  // would 400 and show an ugly error message to a district evaluator on
  // their very first tap. Instead we force the app into demo mode
  // (localStorage-only, no reads or writes against Supabase), navigate
  // to the dashboard, and let the DemoModeBanner tell the visitor
  // they're in demo mode and how to flip back to live.
  //
  // Net effect: this button ALWAYS works — no network dependency, no
  // schema dependency, no auth dependency. The one thing that matters
  // for a district demo: "watch, one tap and you're in the app."
  const enterDemo = async (account) => {
    setDemoBusyRole(account.role);
    setErrors({});

    try {
      // Sign out of any live Supabase session so it doesn't quietly
      // override the demo session when contexts re-fetch.
      if (supabaseLive && supabase) {
        try {
          await supabase.auth.signOut();
        } catch {
          // A stale session that fails to sign out doesn't block demo entry.
        }
      }

      setForceDemoMode();

      // Small pause so the button visibly transitions to "loading" state.
      // Prevents the "did my tap register?" confusion on slow-fingered
      // users who don't see instant feedback.
      await new Promise((resolve) => setTimeout(resolve, 300));

      localStorage.setItem(
        'userSession',
        JSON.stringify({
          email: account.email,
          role: account.role,
          loginTime: new Date().toISOString(),
          rememberMe: false,
          backend: 'demo',
          isDemo: true,
        }),
      );
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        general:
          error?.message ||
          "Couldn't start the demo. Please check your connection and try again.",
      });
    } finally {
      setDemoBusyRole(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email))
      newErrors.email = 'Please enter a valid email address';
    if (!formData?.password) newErrors.password = 'Password is required';
    else if (formData?.password?.length < 6)
      newErrors.password = 'Password must be at least 6 characters';
    if (!formData?.role) newErrors.role = 'Please select your role';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      if (supabaseLive && supabase) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData?.email,
          password: formData?.password,
        });
        if (error) {
          setErrors({
            general:
              error.message ||
              'Invalid email or password. Please try again.',
          });
          return;
        }
        localStorage.setItem(
          'userSession',
          JSON.stringify({
            email: data?.user?.email || formData?.email,
            role: formData?.role,
            userId: data?.user?.id || null,
            loginTime: new Date().toISOString(),
            rememberMe: formData?.rememberMe,
            backend: 'supabase',
          }),
        );
        navigate('/dashboard');
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 400));
      const account = DEMO_ACCOUNTS.find((a) => a.role === formData?.role);
      if (
        account &&
        formData?.email === account.email &&
        formData?.password === account.password
      ) {
        localStorage.setItem(
          'userSession',
          JSON.stringify({
            email: formData?.email,
            role: formData?.role,
            loginTime: new Date().toISOString(),
            rememberMe: formData?.rememberMe,
            backend: 'demo',
          }),
        );
        navigate('/dashboard');
      } else {
        setErrors({
          general:
            "That email + password combination didn't match. Try one of the demo buttons above, or check for a typo.",
        });
      }
    } catch (error) {
      setErrors({
        general:
          error?.message ||
          "Couldn't sign in. Please check your connection and try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  // A real customer signing into a live deployment should see the sign-in
  // form immediately — burying it under demo buttons was the mistake we
  // just fixed. Prospects who want to poke around can still one-tap
  // through the demo section below.
  const signInForm = (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <Select
        label="What's your job?"
        placeholder="Pick the one that fits you"
        options={roleOptions}
        value={formData?.role}
        onChange={(value) => handleInputChange('role', value)}
        error={errors?.role}
        required
      />

      <Input
        label="Email address"
        type="email"
        placeholder="name@school.edu"
        value={formData?.email}
        onChange={(e) => handleInputChange('email', e?.target?.value)}
        error={errors?.email}
        required
      />

      <Input
        label="Password"
        type="password"
        placeholder="Your password"
        value={formData?.password}
        onChange={(e) => handleInputChange('password', e?.target?.value)}
        error={errors?.password}
        required
      />

      <div className="flex items-center justify-between">
        <Checkbox
          label="Remember me"
          checked={formData?.rememberMe}
          onChange={(e) =>
            handleInputChange('rememberMe', e?.target?.checked)
          }
        />

        <Button
          variant="link"
          type="button"
          onClick={handleForgotPassword}
          className="text-sm text-primary hover:text-primary/80"
        >
          Forgot password?
        </Button>
      </div>

      <Button
        type="submit"
        variant="default"
        size="xl"
        loading={isLoading}
        fullWidth
        iconName={isLoading ? 'Loader2' : 'LogIn'}
        iconPosition="left"
        className="mt-2 text-base"
      >
        {isLoading ? 'Signing in…' : 'Sign in'}
      </Button>
    </form>
  );

  const demoSection = (
    <section>
      <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4">
        <div className="mb-1 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
            <Icon name="Eye" size={11} />
            Demo — no account required
          </span>
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          Just exploring? Take a tour.
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          Tap a role below to open the app with sample students, IEP
          goals, and behavior data already filled in. Nothing you do in
          the demo is saved — it's a safe way to click around before you
          buy.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DEMO_ACCOUNTS.map((account) => (
            <button
              key={account.role}
              type="button"
              onClick={() => enterDemo(account)}
              disabled={demoBusyRole !== null}
              className={`group flex items-center gap-3 rounded-xl p-4 text-left text-white shadow-md transition-all duration-150 focus:outline-none focus:ring-4 focus:ring-primary/30 disabled:opacity-70 disabled:cursor-not-allowed ${account.color}`}
              aria-label={`Preview the app as a ${account.role.replace('_', ' ')}`}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                {demoBusyRole === account.role ? (
                  <Icon name="Loader2" size={20} className="animate-spin" />
                ) : (
                  <Icon name={account.icon} size={22} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base leading-tight">
                  {account.label}
                </p>
                <p className="text-xs text-white/85 leading-tight mt-0.5">
                  {account.description}
                </p>
              </div>
              <Icon
                name="ArrowRight"
                size={18}
                className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );

  const divider = (label) => (
    <div className="my-6 flex items-center gap-3">
      <div className="flex-1 border-t border-border" />
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="flex-1 border-t border-border" />
    </div>
  );

  return (
    <div className="w-full max-w-md mx-auto">
      {errors?.general && (
        <div
          role="alert"
          className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg"
        >
          <div className="flex items-start gap-2">
            <Icon
              name="AlertCircle"
              size={20}
              className="text-red-600 mt-0.5 flex-shrink-0"
            />
            <p className="text-base text-red-800">{errors?.general}</p>
          </div>
        </div>
      )}

      {supabaseLive ? (
        // Live deployment: real customers arrive with credentials. Show
        // the sign-in form first, then demo section as a clearly-labeled
        // secondary option for prospects.
        <>
          <section>
            <h3 className="text-lg font-semibold text-foreground mb-1">
              Sign in to your account
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Use the email and password your district set up for you.
            </p>
            {signInForm}
          </section>
          {divider('or')}
          {demoSection}
        </>
      ) : (
        // No live Supabase: this is a demo-only build (e.g. the
        // pre-sale marketing URL). Lead with the demo buttons so
        // visitors can look around instantly, and hide the sign-in
        // form behind a link so it doesn't clutter the primary path.
        <>
          {demoSection}
          {divider('already have an account?')}
          {!showSignIn ? (
            <button
              type="button"
              onClick={() => setShowSignIn(true)}
              className="w-full text-center text-sm text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              Sign in with your account →
            </button>
          ) : (
            <section>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                Sign in
              </h3>
              {signInForm}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Icon
                    name="Info"
                    size={16}
                    className="text-blue-600 mt-0.5"
                  />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">
                      Trying the demo credentials manually?
                    </p>
                    <p className="mb-2 text-xs">
                      The one-tap demo buttons above sign you in with
                      these same accounts.
                    </p>
                    <ul className="text-xs space-y-0.5 font-mono">
                      {DEMO_ACCOUNTS.map((a) => (
                        <li key={a.role}>
                          <strong>{a.label.replace('Try demo — ', '')}:</strong>{' '}
                          {a.email} / {a.password}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default LoginForm;
